import type {
  AnalyticsClient,
  AnalyticsEventKey,
  AnalyticsEventProperties,
} from "@healthos/analytics";
import { logBranchEvent, resetBranch } from "./branch";
import { isInDesktopEmbed } from "./host-auth";

/**
 * Branch adapter for the shared event contract.
 *
 * Branch is an attribution vendor, not a third analytics warehouse, so this
 * sink is the narrowest of the three by a wide margin. Segment and Datadog
 * take the whole catalog and each decides per event (`analyticsEventGoesTo`);
 * Branch takes only what it needs to close the loop from a download surface to
 * an install, and the mapping below is the entire list.
 *
 * ## Why an allowlist rather than a sink flag
 *
 * The other two sinks are opt-out: a new event reaches them unless its spec
 * narrows `sinks`. Adding `"branch"` to `AnalyticsSink` would inherit that
 * default and quietly ship every future event to an attribution vendor, which
 * is the opposite of the posture a Covered Entity surface needs. So Branch is
 * opt-in, by name, here.
 *
 * ## Why no properties travel
 *
 * The catalog is PHI-safe but not PHI-free of context: `lens` is the health
 * focus a member asked us to watch, which is why several events already mark
 * it `datadogOnlyProperties`. Rather than maintain a second per-property
 * allowlist that has to stay correct as the catalog grows, nothing
 * member-derived is forwarded at all. Each Branch event carries its constant
 * name and `client_surface`, both of ours. Branch's own value here is the
 * click-to-install join, which needs no properties from us.
 */

/**
 * HealthOS event key to Branch event name.
 *
 * `INITIATE_PURCHASE` is a Branch standard commerce event and is deliberately
 * not used: nothing here is a purchase, and a standard name would land these
 * in Branch's revenue reporting. Custom names keep them in the custom-event
 * stream where they belong.
 */
const BRANCH_EVENT_NAMES: Partial<Record<AnalyticsEventKey, string>> = {
  companionPromptSelected: "healthos_companion_install_intent",
};

/**
 * Event keys where only some values are worth an attribution event.
 *
 * `companionPromptSelected` fires for `dismiss` as well as `open_store`, and a
 * dismissal is the absence of install intent. Sending both would make the
 * Branch funnel's top-of-funnel count meaningless.
 */
function isAttributableEvent<Key extends AnalyticsEventKey>(
  key: Key,
  properties: AnalyticsEventProperties[Key],
): boolean {
  if (key === "companionPromptSelected") {
    return (
      (properties as AnalyticsEventProperties["companionPromptSelected"]).action === "open_store"
    );
  }
  return true;
}

export const branchAnalytics: AnalyticsClient = {
  /**
   * Deliberately empty. Pushing a member id into Branch's identity graph needs
   * a signed BAA first; see the `setIdentity` note in `branch.ts`.
   */
  identify() {},
  /**
   * Shared-browser hygiene. Branch keeps its session in localStorage, so a
   * sign-out has to end it or the next member on the machine inherits it.
   */
  reset() {
    resetBranch();
  },
  /**
   * Deliberately empty. Branch has no page-view concept we want, and route
   * names are product surface detail an attribution vendor has no need for.
   */
  page() {},
  track<Key extends AnalyticsEventKey>(key: Key, properties: AnalyticsEventProperties[Key]) {
    const name = BRANCH_EVENT_NAMES[key];
    if (!name || !isAttributableEvent(key, properties)) return;
    logBranchEvent(name, { client_surface: isInDesktopEmbed() ? "desktop" : "web" });
  },
};
